import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import FaqAccordion from "@/components/FaqAccordion";
import JsonLd from "@/components/JsonLd";
import { DoctolibLogo } from "@/components/DoctolibLogo";
import useSeo, { SITE_URL } from "@/hooks/useSeo";
import { SITE } from "@/constants/site";
import useI18n from "@/i18n";
import { HTML_LANG, pagePath, SOIN_KEYS } from "@/i18n/config";

const Liste = ({ items }) => (
    <ul className="mt-4 space-y-2">
        {items.map((item) => (
            <li key={item} className="flex gap-2.5 text-ink2">
                <span aria-hidden="true" style={{ color: "var(--c-terra)" }}>·</span>
                <span>{item}</span>
            </li>
        ))}
    </ul>
);

const Bloc = ({ titre, lead, items, testid }) => (
    <Reveal>
        <section data-testid={testid}>
            <h2 className="text-2xl text-terrat md:text-3xl">{titre}</h2>
            <hr className="rule-gold mt-3 w-12" />
            {lead && <p className="mt-4 text-ink2">{lead}</p>}
            <Liste items={items} />
        </section>
    </Reveal>
);

export default function ConseilsPage() {
    const { t, lang, soins, toSoin } = useI18n();
    const page = t.pages.conseils;
    const path = pagePath(lang, "conseils");

    useSeo({
        title: t.seo.conseils.title,
        description: t.seo.conseils.description,
        path,
        lang,
        pageKey: "conseils",
    });

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <JsonLd
                id="ld-conseils"
                data={{
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "MedicalWebPage",
                            name: t.seo.conseils.title,
                            description: t.seo.conseils.description,
                            url: `${SITE_URL}${path}`,
                            inLanguage: HTML_LANG[lang],
                            audience: { "@type": "Patient" },
                            lastReviewed: "2026-11-04",
                            provider: {
                                "@type": "MedicalClinic",
                                name: SITE.name,
                                url: `${SITE_URL}${pagePath(lang, "home")}`,
                            },
                        },
                        {
                            "@type": "FAQPage",
                            mainEntity: page.faq.map(([q, a]) => ({
                                "@type": "Question",
                                name: q,
                                acceptedAnswer: { "@type": "Answer", text: a },
                            })),
                        },
                    ],
                }}
            />
            <Header />
            <main className="pt-24" data-testid="page-conseils">
                <div className="mx-auto max-w-[840px] px-5 pb-20 pt-6">
                    <PageBreadcrumb label={t.nav.conseils} pageKey="conseils" />

                    <Reveal>
                        <p className="eyebrow mt-8">{page.eyebrow}</p>
                        <h1 className="title-grad mt-4 text-4xl sm:text-5xl">{page.h1}</h1>
                        <hr className="rule-gold mt-5 w-24" />
                        <p className="mt-5 max-w-[62ch] text-lg text-ink2">{page.lead}</p>
                    </Reveal>

                    <div className="mt-14 space-y-12">
                        <Bloc
                            titre={page.beforeTitle}
                            lead={page.beforeLead}
                            items={page.beforeItems}
                            testid="conseils-avant"
                        />
                        <Bloc
                            titre={page.laserTitle}
                            items={page.laserItems}
                            testid="conseils-laser"
                        />
                        <Bloc
                            titre={page.peelTitle}
                            items={page.peelItems}
                            testid="conseils-peeling"
                        />
                        <Bloc
                            titre={page.injTitle}
                            items={page.injItems}
                            testid="conseils-injections"
                        />

                        <Reveal>
                            <section data-testid="conseils-apres">
                                <h2 className="text-2xl text-terrat md:text-3xl">
                                    {page.afterTitle}
                                </h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <div className="mt-4 space-y-4 text-ink2">
                                    {page.afterParagraphs.map((p) => (
                                        <p key={p.slice(0, 40)}>{p}</p>
                                    ))}
                                </div>
                                <Liste items={page.afterItems} />
                            </section>
                        </Reveal>

                        <Reveal>
                            <section data-testid="conseils-soleil">
                                <h2 className="text-2xl text-terrat md:text-3xl">
                                    {page.soleilTitle}
                                </h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <div className="mt-4 space-y-4 text-ink2">
                                    {page.soleilParagraphs.map((p) => (
                                        <p key={p.slice(0, 40)}>{p}</p>
                                    ))}
                                </div>
                            </section>
                        </Reveal>

                        <Reveal>
                            <section className="card p-7" data-testid="conseils-alerte">
                                <h2 className="text-2xl text-terrat md:text-3xl">
                                    {page.alerteTitle}
                                </h2>
                                <hr className="rule-gold mt-3 w-12" />
                                <p className="mt-4 text-ink2">{page.alerteLead}</p>
                                <Liste items={page.alerteItems} />
                                <p className="mt-4 font-semibold text-olivet">{page.alerteFin}</p>
                            </section>
                        </Reveal>
                    </div>

                    <div className="mt-16">
                        <Reveal>
                            <h2 className="text-2xl text-terrat md:text-3xl">{page.faqTitle}</h2>
                            <hr className="rule-gold mt-3 w-12" />
                        </Reveal>
                        <div className="mt-6">
                            <FaqAccordion items={page.faq} prefix="faq-conseils" />
                        </div>
                    </div>

                    <Reveal>
                        <div className="card mt-16 p-7" data-testid="conseils-cta">
                            <h2 className="text-2xl text-terrat">{t.pages.soin.ctaTitle}</h2>
                            <hr className="rule-gold mt-3 w-12" />
                            <p className="mt-4 text-ink2">{t.pages.soin.ctaText}</p>
                            <a
                                href={SITE.doctolibUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary mt-6"
                                data-testid="conseils-rdv-button"
                            >
                                {t.common.rdvOn}
                                <DoctolibLogo height={15} color="#FFFFFF" />
                            </a>
                        </div>
                    </Reveal>

                    <Reveal>
                        <div className="mt-14">
                            <h2 className="text-xl text-olivet">{t.pages.soin.relatedTitle}</h2>
                            <hr className="rule-gold mt-3 w-12" />
                            <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2.5">
                                {SOIN_KEYS.map((key) => (
                                    <li key={key}>
                                        <Link
                                            to={toSoin(key)}
                                            className="link-underline text-sm font-bold text-terrat"
                                            data-testid={`conseils-soin-link-${key}`}
                                        >
                                            {soins[key].nav}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>

                    <Reveal>
                        <p className="mt-12 max-w-[72ch] text-sm italic text-ink2">
                            {t.pages.soin.disclaimer}
                        </p>
                    </Reveal>
                </div>
            </main>
            <Footer />
        </>
    );
}
